// src/pages/Settings.jsx

import React, { useState, useEffect } from 'react';
import {
  FiSave,
  FiLock,
  FiSmartphone,
  FiShield,
  FiRefreshCw,
  FiToggleRight,
  FiX,
  FiKey,
  FiUsers,
  FiUserPlus,
  FiTrash2,
  FiCheck
} from 'react-icons/fi';

import WabaAccountsPanel from '../components/WabaAccountsPanel';

const OTP_LENGTH = 6;

// The old gate compared a hard-coded '6363' in the browser, which shipped in
// the JS bundle and gated nothing — the API answered regardless. The unlock now
// lives in the server session; this screen only drives it.
async function api(path, options = {}) {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { detail: text }; }
  if (!res.ok) throw new Error(body?.detail || body?.message || `HTTP ${res.status}`);
  return body;
}

// One row of the platform-user table. The draft lives here rather than in the
// page so typing in one row does not re-render the whole settings screen, and
// so an unsaved edit is dropped when the list is replaced by a server response.
function UserRow({ row, roles, busy, onSave, onRemove }) {
  const [name, setName] = useState(row.name);
  const [role, setRole] = useState(row.role);
  useEffect(() => { setName(row.name); setRole(row.role); }, [row.name, row.role]);

  const dirty = name.trim() !== row.name || role !== row.role;

  return (
    <tr>
      <td className="py-2 pr-4">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full min-w-[140px] rounded-lg border border-gray-200 px-3 py-1.5 focus:ring-2 focus:ring-brand-500"
        />
      </td>
      <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
        +{row.phone}
        {row.is_you && (
          <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
            you
          </span>
        )}
      </td>
      <td className="py-2 pr-4">
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 focus:ring-2 focus:ring-brand-500"
        >
          {(roles || ['admin', 'operator']).map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </td>
      <td className="py-2">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={!dirty || busy}
            onClick={() => onSave({ name: name.trim(), role })}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            <FiCheck className="h-4 w-4" />
            Save
          </button>
          <button
            type="button"
            disabled={busy || row.is_you}
            title={row.is_you ? 'You cannot remove your own access' : 'Remove this user'}
            onClick={onRemove}
            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function Settings() {
  // — OTP gating —
  const [gate, setGate] = useState(null);          // null while status loads
  const [unlocked, setUnlocked] = useState(false);
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const loadGate = () =>
    api('/settings/otp/status')
      .then(s => { setGate(s); setUnlocked(Boolean(s.unlocked)); })
      .catch(e => { setGate({ configured: false, hint: e.message }); });

  useEffect(() => { loadGate(); }, []);

  // Resend cooldown, mirroring the server's own limit so the button does not
  // invite a request that will only come back as 429.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async () => {
    setSending(true); setOtpError(''); setOtpNotice('');
    try {
      const out = await api('/settings/otp/request', { method: 'POST' });
      setOtpNotice(out.message);
      setCodeSent(true);
      setCooldown(30);
      setDigits(Array(OTP_LENGTH).fill(''));
      setTimeout(() => document.getElementById('otp-0')?.focus(), 50);
    } catch (e) { setOtpError(e.message); } finally { setSending(false); }
  };

  const verify = async (code) => {
    setVerifying(true); setOtpError('');
    try {
      await api('/settings/otp/verify', {
        method: 'POST', body: JSON.stringify({ code }),
      });
      setUnlocked(true);
    } catch (e) {
      setOtpError(e.message);
      setDigits(Array(OTP_LENGTH).fill(''));
      document.getElementById('otp-0')?.focus();
    } finally { setVerifying(false); }
  };

  const onDigit = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    // Pasting the whole code into the first box should just work.
    if (val.length > 1) {
      const spread = val.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
      const next = Array(OTP_LENGTH).fill('');
      spread.forEach((d, i) => { next[i] = d; });
      setDigits(next);
      setOtpError('');
      if (spread.length === OTP_LENGTH) verify(next.join(''));
      return;
    }
    const next = [...digits];
    next[idx] = val.slice(-1);
    setDigits(next);
    setOtpError('');
    if (val && idx < OTP_LENGTH - 1) document.getElementById(`otp-${idx + 1}`)?.focus();
    if (next.every(d => d !== '')) verify(next.join(''));
  };

  const onDigitKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      document.getElementById(`otp-${idx - 1}`)?.focus();
    }
  };

  const lock = async () => {
    try { await api('/settings/lock', { method: 'POST' }); } catch { /* ignore */ }
    setUnlocked(false);
    setCodeSent(false);
    setDigits(Array(OTP_LENGTH).fill(''));
    setOtpNotice('');
    loadGate();
  };

  // — Settings logic (only runs once unlocked) —
  const [fullSettings, setFullSettings] = useState(null);
  const [formData, setFormData] = useState({
    whatsappApiUrl: '',
    phoneNumberId: '',
    accessToken: '',
    businessAccountId: '',
    retryAttempts: 1,
    demoMode: 'false',
    demoKafka: 'false',
    demoWhatsapp: 'false',
    geminiApiKey: ''
  });
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Live check of the Gemini key the OCR service is *actually* using. It reads
  // the key from the settings file on every request, so this reports on what
  // was last saved — not on what is typed in the box. The button says so.
  const [geminiCheck, setGeminiCheck] = useState({ busy: false, result: null, error: '' });

  const verifyGeminiKey = async () => {
    setGeminiCheck({ busy: true, result: null, error: '' });
    try {
      const out = await api('/aiocr/health/gemini');
      setGeminiCheck({ busy: false, result: out, error: '' });
    } catch (err) {
      setGeminiCheck({ busy: false, result: null, error: err.message });
    }
  };

  // fetch only once unlocked. /settings/reveal is the gated read; plain
  // /settings stays open because the dispatcher and consumer poll it.
  useEffect(() => {
    if (!unlocked) return;
    fetch('/api/settings/reveal', { cache: 'no-store', credentials: 'include' })
      .then(res =>
        res.ok
          ? res.json()
          : Promise.reject(new Error(`HTTP ${res.status}`))
      )
      .then(data => {
        if (!data.success) throw new Error('API error');
        const s = data.settings;
        setFullSettings(s);
        setFormData({
          whatsappApiUrl: s.WHATSAPP_API_URL || '',
          phoneNumberId: s.WHATSAPP_PHONE_NUMBER_ID || '',
          accessToken: s.WHATSAPP_ACCESS_TOKEN || '',
          businessAccountId: s.WHATSAPP_BUSINESS_ACCOUNT_ID || '',
          retryAttempts: Number(s.RETRY_MAX_ATTEMPTS) || 1,
          demoMode: String(s.DEMO_MODE),
          demoKafka: String(s.DEMO_KAFKA),
          demoWhatsapp: String(s.DEMO_WHATSAPP),
          geminiApiKey: s.GEMINI_API_KEY || ''
        });
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
      });
  }, [unlocked]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value }));
  };

  // — Platform users (AUTH_ALLOWED_PHONES) —
  // Admin only on the server; an operator simply gets a 403 here and the
  // section explains itself rather than disappearing without a reason.
  const [users, setUsers] = useState(null);
  const [usersError, setUsersError] = useState('');
  const [usersNotice, setUsersNotice] = useState('');
  const [usersBusy, setUsersBusy] = useState(false);
  const [newUser, setNewUser] = useState({ phone: '', name: '', role: 'operator' });

  const applyUsers = out => {
    setUsers(out);
    setUsersNotice(out.message || '');
    setUsersError('');
  };

  useEffect(() => {
    if (!unlocked) return;
    api('/settings/users')
      .then(out => { setUsers(out); })
      .catch(e => { setUsersError(e.message); });
  }, [unlocked]);

  const addUser = async e => {
    e.preventDefault();
    setUsersBusy(true); setUsersError(''); setUsersNotice('');
    try {
      applyUsers(await api('/settings/users', {
        method: 'POST', body: JSON.stringify(newUser),
      }));
      setNewUser({ phone: '', name: '', role: 'operator' });
    } catch (err) { setUsersError(err.message); } finally { setUsersBusy(false); }
  };

  const saveUser = async (phone, patch) => {
    setUsersBusy(true); setUsersError(''); setUsersNotice('');
    try {
      applyUsers(await api(`/settings/users/${phone}`, {
        method: 'PUT', body: JSON.stringify(patch),
      }));
    } catch (err) { setUsersError(err.message); } finally { setUsersBusy(false); }
  };

  const removeUser = async row => {
    if (!window.confirm(`Remove ${row.name}? They will not be able to sign in again.`)) return;
    setUsersBusy(true); setUsersError(''); setUsersNotice('');
    try {
      applyUsers(await api(`/settings/users/${row.phone}`, { method: 'DELETE' }));
    } catch (err) { setUsersError(err.message); } finally { setUsersBusy(false); }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!fullSettings) return;
    const payload = {
      KAFKA_BOOTSTRAP_SERVERS: fullSettings.KAFKA_BOOTSTRAP_SERVERS,
      KAFKA_TOPIC: fullSettings.KAFKA_TOPIC,
      KAFKA_GROUP_ID: fullSettings.KAFKA_GROUP_ID,
      KAFKA_AUTO_OFFSET_RESET: fullSettings.KAFKA_AUTO_OFFSET_RESET,
      WHATSAPP_API_URL: formData.whatsappApiUrl,
      WHATSAPP_PHONE_NUMBER_ID: formData.phoneNumberId,
      WHATSAPP_ACCESS_TOKEN: formData.accessToken,
      WHATSAPP_BUSINESS_ACCOUNT_ID: formData.businessAccountId,
      RETRY_MAX_ATTEMPTS: formData.retryAttempts,
      RETRY_BACKOFF_FACTOR: parseFloat(fullSettings.RETRY_BACKOFF_FACTOR),
      LOG_LEVEL: fullSettings.LOG_LEVEL,
      DEMO_MODE: formData.demoMode === 'true',
      DEMO_KAFKA: formData.demoKafka === 'true',
      DEMO_WHATSAPP: formData.demoWhatsapp === 'true',
      GEMINI_API_KEY: formData.geminiApiKey
    };

    fetch('/api/settings', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => {
        // The unlock expires on the server; send the operator back to the code
        // screen rather than leaving a Save button that silently does nothing.
        if (res.status === 403) {
          setUnlocked(false);
          setCodeSent(false);
          loadGate();
        }
        return res.ok
          ? res.json()
          : res.json().then(err => Promise.reject(new Error(err.detail || JSON.stringify(err))));
      })
      .then(d => {
        if (d.success) {
          setShowSuccessModal(true);
        } else {
          setError(d.message || 'Unknown error saving settings');
        }
      })
      .catch(err => {
        setError(err.message);
      });
  };

  // — Render: OTP screen or settings screen —
  if (!unlocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl sm:p-8">
          <div className="mb-6 text-center">
            <FiKey className="mx-auto mb-4 h-12 w-12 text-brand-700" />
            <h1 className="mb-2 text-2xl font-bold text-gray-900">Verify it's you</h1>
            <p className="text-gray-600">
              {gate === null
                ? 'Checking…'
                : !gate.configured
                  ? 'There is no WhatsApp number to send a code to.'
                  : codeSent
                    ? `Enter the ${OTP_LENGTH}-digit code sent to ${gate.masked_phone} on WhatsApp.`
                    : gate.recipient === 'session'
                      // The number they signed in with, so say so — otherwise a
                      // masked number on its own reads like someone else's.
                      ? `A one-time code will be sent on WhatsApp to your number, ${gate.masked_phone}.`
                      : `A one-time code will be sent on WhatsApp to ${gate.masked_phone}.`}
            </p>
          </div>

          {gate && !gate.configured && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {gate.hint || 'Sign in with your WhatsApp number, or ask an administrator to configure SETTINGS_OTP_PHONE.'}
            </div>
          )}

          {gate?.configured && !codeSent && (
            <button
              onClick={sendCode}
              disabled={sending}
              className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 font-semibold text-ink hover:bg-brand-600 disabled:opacity-60"
            >
              <FiSmartphone className="h-5 w-5" />
              {sending ? 'Sending…' : 'Send code on WhatsApp'}
            </button>
          )}

          {gate?.configured && codeSent && (
            <>
              <div className="mb-4 flex justify-center gap-2">
                {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="tel"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={i === 0 ? OTP_LENGTH : 1}
                    value={digits[i]}
                    onChange={e => onDigit(i, e.target.value)}
                    onKeyDown={e => onDigitKeyDown(i, e)}
                    disabled={verifying}
                    className="h-14 w-11 rounded-lg border-2 border-gray-200 text-center text-2xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 sm:h-16 sm:w-12"
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              {verifying && (
                <p className="mb-2 text-center text-sm text-gray-500">Verifying…</p>
              )}

              <button
                onClick={sendCode}
                disabled={sending || cooldown > 0}
                className="min-h-[44px] w-full rounded-xl border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? 'Sending…' : 'Resend code'}
              </button>
            </>
          )}

          {otpNotice && !otpError && (
            <p className="mt-4 text-center text-sm text-green-700">{otpNotice}</p>
          )}
          {otpError && (
            <p className="mt-4 text-center text-sm text-red-600">{otpError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-full">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                <FiShield className="h-6 w-6" style={{ color: '#F96168' }} />
                Application Settings
              </h1>
              <button
                type="button"
                onClick={lock}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <FiLock className="h-4 w-4" />
                Lock
              </button>
            </div>

            {error && (
              <div className="mb-4 text-red-600">
                Error: {error}
              </div>
            )}

            {!fullSettings ? (
              <div className="py-10 text-center text-gray-600">
                Loading settings…
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* WhatsApp Business API */}
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiSmartphone className="w-5 h-5 text-gray-600" />
                    WhatsApp Business API
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="space-y-2 lg:col-span-2">
                      <label className="text-sm font-medium text-gray-700">
                        API Base URL
                      </label>
                      <input
                        type="url"
                        name="whatsappApiUrl"
                        value={formData.whatsappApiUrl}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Phone Number ID
                      </label>
                      <input
                        type="text"
                        name="phoneNumberId"
                        value={formData.phoneNumberId}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                    </div>
                    <div className="space-y-2 lg:col-span-3">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <FiLock className="w-4 h-4" />
                        Access Token
                      </label>
                      <input
                        type="password"
                        name="accessToken"
                        value={formData.accessToken}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                    </div>
                    <div className="space-y-2 lg:col-span-2">
                      <label className="text-sm font-medium text-gray-700">
                        Business Account ID
                      </label>
                      <input
                        type="text"
                        name="businessAccountId"
                        value={formData.businessAccountId}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Application Settings */}
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiRefreshCw className="w-5 h-5 text-gray-600" />
                    Application Settings
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Max Retry Attempts
                      </label>
                      <input
                        type="number"
                        name="retryAttempts"
                        value={formData.retryAttempts}
                        onChange={handleChange}
                        min="1"
                        max="10"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Demo Mode Flags */}
                <div className="pb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiToggleRight className="w-5 h-5 text-gray-600" />
                    Demo Mode Flags
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {['demoMode','demoKafka','demoWhatsapp'].map(key => (
                      <div className="space-y-2" key={key}>
                        <label className="text-sm font-medium text-gray-700">
                          {key === 'demoMode' ? 'Demo Mode'
                            : key === 'demoKafka' ? 'Demo Kafka'
                            : 'Demo WhatsApp'}
                        </label>
                        <select
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
                        >
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* AI OCR Settings */}
		<div className="border-b border-gray-200 pb-6">
		  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
		    <FiKey className="w-5 h-5 text-gray-600" />
		    AI OCR Settings
		  </h2>
		  <p className="mb-4 text-sm text-gray-500">
		    A Google AI Studio key (aistudio.google.com/apikey). The OCR APIs
		    (<code>/api/aiocr</code>, <code>/api/invoice-ocr</code>, <code>/api/audio-order</code>, <code>/api/sales-voice-decode</code>, <code>/api/lead-score</code>)
		    read it on every request, so a new key takes effect as soon as it is
		    saved — no restart. Save first, then verify.
		  </p>
		  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
		    <div className="space-y-2 lg:col-span-2">
		      <label className="text-sm font-medium text-gray-700">
			Gemini AI Key
		      </label>
		      <input
			type="password"
			name="geminiApiKey"
			value={formData.geminiApiKey}
			onChange={handleChange}
			autoComplete="off"
			placeholder="AIza…"
			className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand-500"
			required
		      />
		      {formData.geminiApiKey && (/[\s"']/.test(formData.geminiApiKey) || formData.geminiApiKey.length < 20) && (
			<p className="text-xs text-amber-700">
			  This contains spaces or quotes, or is too short to be a key. Paste only the
			  key value itself, with nothing around it.
			</p>
		      )}
		    </div>
		    <div className="space-y-2">
		      <label className="text-sm font-medium text-gray-700">Saved key</label>
		      <button
			type="button"
			onClick={verifyGeminiKey}
			disabled={geminiCheck.busy}
			title="Ask the OCR service to check the key it currently has against Gemini"
			className="w-full px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
		      >
			{geminiCheck.busy ? 'Checking…' : 'Verify saved key'}
		      </button>
		    </div>
		  </div>
		  {geminiCheck.error && (
		    <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
		      Could not check the key: {geminiCheck.error}
		    </div>
		  )}
		  {geminiCheck.result && (
		    <div
		      className={`mt-3 rounded-xl border p-3 text-sm ${
			geminiCheck.result.key_valid && geminiCheck.result.model_available
			  ? 'border-green-200 bg-green-50 text-green-800'
			  : 'border-amber-200 bg-amber-50 text-amber-800'
		      }`}
		    >
		      <div className="font-medium">
			{!geminiCheck.result.configured
			  ? 'No key saved yet'
			  : geminiCheck.result.key_valid && geminiCheck.result.model_available
			    ? `Key ${geminiCheck.result.key_hint || ''} works — model ${geminiCheck.result.model} is available`
			    : geminiCheck.result.key_valid === false
			      ? `Gemini rejected key ${geminiCheck.result.key_hint || ''}`
			      : 'Could not confirm the key'}
		      </div>
		      <div className="mt-1 text-xs opacity-80">{geminiCheck.result.detail}</div>
		    </div>
		  )}
		</div>


                {/* Save */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    style={{ backgroundColor: '#F96168', color: 'white' }}
                  >
                    <FiSave className="w-4 h-4" />
                    Save Settings
                  </button>
                </div>
              </form>
            )}

            {/* The WhatsApp accounts the platform can send from. Outside the
                settings form for the same reason the user table is: it has a
                form of its own, and forms cannot nest. */}
            <WabaAccountsPanel
              onLocked={() => { setUnlocked(false); setCodeSent(false); loadGate(); }}
            />

            {/* Platform users — kept outside the settings form on purpose:
                a form cannot be nested inside another one. */}
            <div className="mt-8 border-t border-gray-200 pt-6">
              <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <FiUsers className="h-5 w-5 text-gray-600" />
                Platform Users
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                Anyone listed here can sign in with a one-time code sent to their
                WhatsApp number. Changes take effect immediately.
              </p>

              {usersError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {usersError}
                </div>
              )}
              {usersNotice && !usersError && (
                <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                  {usersNotice}
                </div>
              )}
              {users === null && !usersError ? (
                <div className="py-6 text-center text-gray-500">Loading users…</div>
              ) : users ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left text-sm">
                      <thead className="text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="py-2 pr-4 font-medium">Name</th>
                          <th className="py-2 pr-4 font-medium">WhatsApp number</th>
                          <th className="py-2 pr-4 font-medium">Role</th>
                          <th className="py-2 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {users.users.map(row => (
                          <UserRow
                            key={row.phone}
                            row={row}
                            roles={users.roles}
                            busy={usersBusy}
                            onSave={patch => saveUser(row.phone, patch)}
                            onRemove={() => removeUser(row)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <form
                    onSubmit={addUser}
                    className="mt-6 grid grid-cols-1 gap-4 rounded-xl bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-4"
                  >
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        WhatsApp number
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        placeholder="919812345678"
                        value={newUser.phone}
                        onChange={e => setNewUser(u => ({ ...u, phone: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-brand-500"
                        required
                      />
                      <p className="text-xs text-gray-500">
                        With the country code, digits only.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Name</label>
                      <input
                        type="text"
                        placeholder="Who is this?"
                        value={newUser.name}
                        onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-brand-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Role</label>
                      <select
                        value={newUser.role}
                        onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-brand-500"
                      >
                        {(users.roles || ['admin', 'operator']).map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="submit"
                        disabled={usersBusy}
                        className="flex min-h-[42px] w-full items-center justify-center gap-2 rounded-lg px-4 font-semibold text-white disabled:opacity-60"
                        style={{ backgroundColor: '#F96168' }}
                      >
                        <FiUserPlus className="h-4 w-4" />
                        {usersBusy ? 'Working…' : 'Add user'}
                      </button>
                    </div>
                  </form>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Settings Saved
              </h3>
              <button onClick={() => setShowSuccessModal(false)}>
                <FiX className="w-6 h-6 text-gray-500 hover:text-gray-800" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-800">
                Settings saved successfully.<br/>
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-brand-500 rounded-lg hover:bg-brand-600 text-ink"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
